// DriverChatBox.jsx (Complete file after modifications)
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  InputGroup,
  Form,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import { FaPaperPlane, FaComments, FaTimes } from "react-icons/fa";
import { driverAPI } from "../../api/apiSystem";
import Pusher from "pusher-js";
import "./ChatBox.css";

// Enable Pusher logging in development
Pusher.logToConsole = process.env.NODE_ENV === "development";

export default function DriverChatBox() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const pusherRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const messagesEndRef = useRef(null);

  const getInitials = (name) => {
    if (!name) return "??";

    const names = name.split(" ");
    if (names.length === 1) {
      return name.substring(0, 2).toUpperCase();
    }

    return (
      names[0].charAt(0) + names[names.length - 1].charAt(0)
    ).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return "#6c757d";

    const colors = [
      "#007bff",
      "#28a745",
      "#dc3545",
      "#fd7e14",
      "#6f42c1",
      "#e83e8c",
      "#20c997",
      "#17a2b8",
      "#ffc107",
      "#6610f2",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const Avatar = ({ name, size = 40, className = "" }) => {
    const initials = getInitials(name);
    const color = getAvatarColor(name);

    return (
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor: color,
          fontSize: size * 0.4,
          minWidth: size,
        }}
      >
        {initials}
      </div>
    );
  };

  const fetchConversations = async () => {
    setLoading(true);
    setError("");

    try {
      console.log("🔄 Fetching conversations...");
      const result = await driverAPI.chat.conversations();

      console.log("📨 Conversations API Response:", result);

      if (result.success) {
        let messagesData = [];

        if (Array.isArray(result.data.data)) {
          messagesData = result.data.data;
        } else if (
          result.data.data &&
          Array.isArray(result.data.data.messages)
        ) {
          messagesData = result.data.data.messages;
        } else if (result.data.data && Array.isArray(result.data.data.data)) {
          messagesData = result.data.data.data;
        } else if (
          result.data.data &&
          Array.isArray(result.data.data.conversations)
        ) {
          result.data.data.conversations.forEach((conv) => {
            if (conv.messages && Array.isArray(conv.messages)) {
              messagesData = [...messagesData, ...conv.messages];
            }
          });
        }

        setMessages(messagesData);

        messagesData.forEach((msg) => {
          processedMessageIds.current.add(msg.id);
        });

        setUnreadCount(result.data.unread_count || 0);

        // Scroll to bottom after loading
        scrollToBottom();
      } else {
        setError(result.message || "Failed to fetch conversations");
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      setError("Failed to fetch conversations");
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (newMessage.trim() === "") return;

    setSending(true);
    try {
      const result = await driverAPI.chat.send({ message: newMessage });

      if (result.success) {
        // Add the new message locally
        const newMsg = {
          id: Date.now(), // Temporary ID
          message: newMessage,
          sender: currentUser,
          receiver: { name: "Support" },
          is_read: true,
          created_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, newMsg]);
        processedMessageIds.current.add(newMsg.id);
        setNewMessage("");
        scrollToBottom();
      } else {
        setError(result.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const markAsRead = async () => {
    try {
      const result = await driverAPI.chat.markAsRead();
      if (result.success) {
        setUnreadCount(0);
        // Update local messages
        setMessages((prev) => prev.map((msg) => ({ ...msg, is_read: true })));
      }
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  useEffect(() => {
    // Fetch current user
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
    }

    fetchConversations();

    // Setup Pusher
    if (currentUser?.id) {
      pusherRef.current = new Pusher("1a6dafa4504c853b7f61", {
        cluster: "mt1",
        forceTLS: true,
        enabledTransports: ["ws", "wss"],
        encrypted: true,
      });

      const channel = pusherRef.current.subscribe(`chat.${currentUser.id}`);

      channel.bind("message-sent", (data) => {
        console.log("📩 New message received:", data);

        if (!processedMessageIds.current.has(data.id)) {
          setMessages((prev) => [...prev, data]);
          processedMessageIds.current.add(data.id);
          scrollToBottom();

          // If chat is closed, increment unread
          if (!open && data.sender.id !== currentUser.id) {
            setUnreadCount((prev) => prev + 1);
          }
        }
      });
    }

    return () => {
      if (pusherRef.current) {
        pusherRef.current.disconnect();
      }
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (open && unreadCount > 0) {
      markAsRead();
    }
  }, [open]);

  const isCurrentUser = (sender) => {
    return sender?.id === currentUser?.id || (sender?.role_name === "Driver" || sender?.role_name === "Driver Company");
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    let str = dateString;
    const ddmmyyyy = str.match(/^(\d{2})[.-](\d{2})[.-](\d{4})\s(.*)$/);
    if (ddmmyyyy) {
      str = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}T${ddmmyyyy[4]}`;
    } else if (/^\d{4}-\d{2}-\d{2}\s/.test(str)) {
      str = str.replace(" ", "T");
    }
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  };

  return (
    <>
      <Button className="chat-toggle-btn" onClick={() => setOpen(!open)}>
        <FaComments size={22} color="#fff" />
        {unreadCount > 0 && (
          <span className="chat-unread-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="chat-box-container">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-header-avatar">
                {/* {getInitials(currentUser?.name || "Driver" || "Driver Company")} */}
                {currentUser?.image ? (
                  <img
                    src={currentUser.image}
                    alt="Driver"
                    className="img-fluid rounded-circle"
                  />
                ) : (
                  currentUser?.name
                    ? currentUser.name.charAt(0).toUpperCase()
                    : "C"
                )}
              </div>
              <div className="chat-header-text">
                <h6>Chat Support</h6>
                <small>Online support</small>
              </div>
            </div>
            <div className="chat-header-actions">
              {unreadCount > 0 && (
                <Badge bg="light" text="dark" pill style={{ fontSize: 11 }}>
                  {unreadCount}
                </Badge>
              )}
              <button className="chat-close-btn" onClick={() => setOpen(false)}>
                <FaTimes size={14} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="chat-body">
            {loading ? (
              <div className="chat-empty-state">
                <Spinner animation="border" style={{ color: "#ff6600" }} />
              </div>
            ) : error ? (
              <Alert variant="danger" className="mx-2 mt-2">
                {error}
              </Alert>
            ) : messages.length === 0 ? (
              <div className="chat-empty-state">
                <div className="empty-icon">💬</div>
                <h6>No messages yet</h6>
                <p>Start the conversation</p>
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isSent = isCurrentUser(msg.sender);

                return (
                  <div
                    key={msg.id || idx}
                    className={`chat-message-row ${isSent ? "sent" : "received"}`}
                  >
                    <div
                      className={`chat-bubble ${isSent ? "sent" : "received"}`}
                    >
                      {!isSent &&
                        (idx === 0 ||
                          isCurrentUser(messages[idx - 1]?.sender)) && (
                          <div className="chat-bubble-sender">
                            {msg.sender?.name || "Support"}
                          </div>
                        )}

                      {msg.message && (
                        <div className="chat-bubble-text">{msg.message}</div>
                      )}

                      <div className="chat-bubble-meta">
                        <span>
                          {msg.created_at
                            ? (() => {
                                const d = parseDate(msg.created_at);
                                return d
                                  ? d.toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : msg.created_at;
                              })()
                            : ""}
                        </span>
                        {isSent && (
                          <span
                            className={`chat-tick ${msg.is_read ? "read" : "sent-tick"}`}
                          >
                            {msg.is_read ? "✓✓" : "✓"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-input-area">
            <div className="chat-input-wrapper">
              <Form.Control
                className="chat-text-input"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
                as="textarea"
                rows={1}
                style={{ resize: "none" }}
              />
            </div>
            <button
              className="chat-send-btn"
              onClick={handleSend}
              disabled={sending || newMessage.trim() === ""}
            >
              {sending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FaPaperPlane size={16} />
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
